import { AddIcon } from "@chakra-ui/icons";
import { Box, Flex, Input } from "@chakra-ui/react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ContextMenu } from "@nens/chakra/components";
import { LayersIcon, RanaButton, RanaContextMenu, RanaModal } from "@nens/ranalib";

import { AddIconControl } from "@/components/add-icon-control";
import { RanaMap } from "@/lib/rana-map";

import { getIsEditable } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";

export function LayerControlHeader({ onClickAddIcon }: { onClickAddIcon: () => void }) {
  const intl = useIntl();
  const [addFolderModalOpen, setAddFolderModalOpen] = useState(false);
  const isEditable = usePublicationStore(getIsEditable);

  const actions = isEditable
    ? [
        {
          label: intl.formatMessage({
            id: "publication.layer.contextmenu.add_layer",
            defaultMessage: "Add layer",
          }),
          action: onClickAddIcon,
          icon: <AddIcon />,
        },
        {
          label: intl.formatMessage({
            id: "publication.layer.contextmenu.add_folder",
            defaultMessage: "Add folder",
          }),
          action: () => setAddFolderModalOpen(true),
          icon: <AddIcon />,
        },
      ]
    : [];

  return (
    <RanaMap.ControlsHeader>
      {addFolderModalOpen ? <AddFolderModal close={() => setAddFolderModalOpen(false)} /> : null}
      <ContextMenu<HTMLDivElement>
        renderMenu={actions?.length ? () => <RanaContextMenu.Actions actions={actions} /> : null}
      >
        {(ref) => (
          <Flex ref={ref} role="group" alignItems="center" justify="space-between" w="full" gap={2}>
            <LayersIcon color="brand.sky" />
            <Box flexGrow={1}>
              <FormattedMessage id="filepreview.layers" defaultMessage="Layers" />
            </Box>
            {isEditable && (
              <>
                <Box visibility="hidden" _groupHover={{ visibility: "visible" }}>
                  <RanaContextMenu actions={actions} />
                </Box>
                <AddIconControl onClick={onClickAddIcon} />
              </>
            )}
          </Flex>
        )}
      </ContextMenu>
    </RanaMap.ControlsHeader>
  );
}

function AddFolderModal({ close }: { close: () => void }) {
  const intl = useIntl();
  const addFolder = usePublicationStore((state) => state.addFolder);

  const [editedName, setEditedName] = useState("");
  const nameIsValid = editedName.trim() !== "";

  function doAddFolder() {
    if (nameIsValid) {
      addFolder(editedName.trim());
      setEditedName("");
      close();
    }
  }

  return (
    <RanaModal
      isOpen={true}
      onClose={close}
      topBar={intl.formatMessage({
        id: "publication.map.addnewfoldermodal.title",
        defaultMessage: "Add new folder",
      })}
      buttons={[
        <RanaButton key="cancel" variant="secondary" onClick={close}>
          Cancel
        </RanaButton>,
        <RanaButton
          key="change"
          variant="primary"
          isDisabled={!nameIsValid}
          onClick={doAddFolder}
          title={
            nameIsValid
              ? undefined
              : intl.formatMessage({
                  id: "publication.map.addnewfolder.disabledwarning",
                  defaultMessage: "Please enter a folder name",
                })
          }
        >
          Add folder
        </RanaButton>,
      ]}
    >
      <Box p={1}>
        <FormattedMessage
          id="publication.map.addnewfolder.message"
          defaultMessage="Enter name for the new folder:"
        />
      </Box>
      <Box p={1}>
        <Input
          value={editedName}
          onChange={(e) => setEditedName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && doAddFolder()}
          autoFocus
        />
      </Box>
    </RanaModal>
  );
}
